<template>
  <pdp-layout>
    <div v-if="isLoyaltyToastBackdropVisible" class="fixed-0 h-full bg-black bg-op-80 lg:hidden" />

    <template #accordions>
      <pdp-section-accordions v-if="!isDigitalProduct" />
    </template>

    <template #breadcrumbs>
      <pdp-section-breadcrumbs />
    </template>

    <template #buy-with-prime>
      <prime-badge
        v-if="$feature.showBuyWithPrimeOnPDP"
        :product-id
        :product-sku="selectedVariant?.id"
        type="deliveryCard"
      />
    </template>

    <template #cta-add-to-cart>
      <div ref="productCtaRef" :class="brandClasses.ctaAddToCart">
        <product-cta
          :add-to-cart-label="bundleAddToCartLabel"
          :attribute-selection
          class="w-full"
          :enable-color-as-attribute
          hide-strikethrough-price
          :include-coming-soon="$feature.showComingSoonOnPDP"
          :include-notify-me="$feature.showNotifyMeOnPDP"
          :include-price="$feature.showPriceInCtas"
          :include-reserve-in-store="$feature.allowProductReservationOnPDP"
          :include-select-size="$feature.showSelectSizeOnPDP"
          :loading="addToCartLoading || inventoryPending || productPending"
          :price="productPrice"
          :product
          :selected-variant
          @add-to-cart="handleAddToCart"
          @notify-me="handleNotifyMe"
          @reserve-in-store="handleInStore('reserve')"
        />
      </div>
    </template>

    <template #cta-apple-pay>
      <client-only>
        <vf-button
          v-if="showApplePay"
          :aria-label="$t.applePayIconText"
          :class="[brandClasses.ctaApplePay, applePayButton.brandClasses?.button]"
          :size="applePayButton.size"
          :variant="applePayButton.variant"
          @click="openTermsAcceptanceDialog"
        >
          <vf-icon v-bind="applePayButton.icon" pos="center" :src="logos.applePay" />
        </vf-button>
      </client-only>
    </template>

    <template #cta-favorites>
      <product-cta-favorites
        v-if="showFavorites"
        :is-favorite
        :pending="isFavoritePending"
        @add="addFavorite"
        @remove="removeFavorite"
      />
    </template>

    <template #cta-find-in-store>
      <product-cta-find-in-store
        v-if="showFindInStore"
        :class="brandClasses.ctaFindInStore"
        @click="handleInStore"
      />
    </template>

    <template #cta-reserve-in-store>
      <product-cta-reserve-in-store
        v-if="showReserveInStore"
        :reserved="enableReserveDialog && isCurrentVariantReserved"
        :variant-id="selectedVariant?.id"
        @change-store="handleInStore('reserve')"
        @click="handleInStore('reserve')"
        @reserve="handleReserve"
      />
    </template>

    <template #espot-above-accordions>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['above-accordions']"
        :class="brandClasses.eSpotAboveAccordions"
        :page-context="eSpotsWithPageContext?.includes('above-accordions')"
        :section="eSpots?.['above-accordions']"
      />
    </template>

    <template #espot-above-add-to-cart>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['above-add-to-cart']"
        :class="brandClasses.eSpotAboveAddToCart"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('above-add-to-cart')"
        :section="eSpots?.['above-add-to-cart']"
      />
    </template>

    <template #espot-above-reviews>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['above-reviews']"
        :class="brandClasses.eSpotAboveReviews"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('above-reviews')"
        :section="eSpots?.['above-reviews']"
      />
    </template>

    <template #espot-below-accordions>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['below-accordions']"
        :class="brandClasses.eSpotBelowAccordions"
        :page-context="eSpotsWithPageContext?.includes('below-accordions')"
        :section="eSpots?.['below-accordions']"
      />
    </template>

    <template #espot-top-of-secondary-section>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['top-of-secondary-section']"
        :class="brandClasses.eSpotTopOfSecondarySection"
        :page-context="eSpotsWithPageContext?.includes('top-of-secondary-section')"
        :section="eSpots?.['top-of-secondary-section']"
      />
    </template>

    <template #espot-below-pricing>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['below-pricing']"
        :class="brandClasses.eSpotBelowPricing"
        :page-context="eSpotsWithPageContext?.includes('below-pricing')"
        :section="eSpots?.['below-pricing']"
      />
    </template>

    <template #espot-below-sizes>
      <cms-section
        v-if="$feature.enablePDPESpots && !isDigitalProduct && eSpots?.['below-sizes']"
        :class="hasVisibleBelowSizesEspot ? brandClasses.eSpotBelowSizes : undefined"
        :section="eSpots?.['below-sizes']"
      />
    </template>

    <template #espot-lazy-above-footer>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['lazy-above-footer']"
        :class="brandClasses.eSpotLazyAboveFooter"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('lazy-above-footer')"
        :section="eSpots?.['lazy-above-footer']"
      />
    </template>

    <template #espot-lazy-below-gallery>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['lazy-below-gallery']"
        :class="brandClasses.eSpotLazyBelowGallery"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('lazy-below-gallery')"
        :section="eSpots?.['lazy-below-gallery']"
      />
    </template>

    <template #fit-finder>
      <pdp-section-fit-finder v-if="showFitFinder" />
    </template>

    <template #fit-rating>
      <pdp-section-fit-rating v-if="showFitRating" />
    </template>

    <template #gallery>
      <pdp-section-gallery />
    </template>

    <template #gift-card>
      <pdp-section-gift-card v-if="isDigitalProduct" />
    </template>

    <template #heading>
      <pdp-section-heading />
    </template>

    <template #klarna-payments>
      <klarna-payments
        v-if="!isDigitalProduct && $feature.showKlarnaOnPDP && product && productPrice && !isOutOfStock && !isNotifyMe"
        :class="brandClasses.klarnaPayments"
        :currency="product.currency"
        :inline="klarnaPaymentsInline"
        :price="productPrice.lowCurrent"
      />
    </template>

    <template #model-measurements>
      <pdp-section-model-measurements v-if="showModelMeasurements" />
    </template>

    <template #notifications>
      <pdp-section-notifications />
    </template>

    <template #paypal-payments>
      <lazy-paypal-payments
        v-if="!isDigitalProduct && $feature.showPaypalOnPDP && !auth.isEmployee && product && productPrice"
        :amount="productPrice.lowCurrent"
        :class="brandClasses.paypalPayments"
        placement="product"
      />
    </template>

    <template #pricing>
      <div v-if="product && productPrice">
        <product-pricing
          :class="brandClasses.headingPrice"
          :currency="product.currency"
          :is-digital-product
          :price="productPrice"
          :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
          :show-omnibus-badge="$feature.showOmnibusBadgeOnQuickshopAndPDP"
          show-sale-price-tooltip
        />
        <p v-if="product.promoExclusionsMessage" class="my-2 text-xs">
          {{ product.promoExclusionsMessage }}
        </p>
      </div>
    </template>

    <template #recommendations>
      <base-lazy-hydrate v-if="$feature.showBloomreachWidget" when="visible">
        <vf-recommendations-widget />
      </base-lazy-hydrate>
    </template>

    <template #reviews>
      <pdp-section-reviews v-if="!isDigitalProduct && $feature.showReviewsOnPDP" />
    </template>

    <template #reviews-summary>
      <pdp-section-reviews-summary
        v-if="!isDigitalProduct && ($feature.showReviewsAverageOnPdp || $feature.showReviewsOnPDP)"
      />
    </template>

    <template #shipping-methods>
      <pdp-section-shipping-methods
        v-if="!isDigitalProduct && $feature.showPickupOptionsOnPDP && isBOPISEnabled"
        ref="shippingMethodsRef"
      />
      <product-shipping-methods-option
        v-else-if="!isDigitalProduct && !$feature.showPickupOptionsOnPDP && isBOPISEnabled && $feature.showEddOnPDP"
        data-test-id="pdp-shipping-methods"
        :description="$t.standardShippingTime"
        icon="shipping"
        :label="$t.shipToHome"
        :selectable="false"
        :show-edd="$feature.showEddOnPDP"
        type="standard"
        :variant="defaultVariant"
      />
    </template>

    <template #shop-the-look>
      <pdp-section-shop-the-look v-if="showShopTheLook" />
    </template>

    <template #size-chart>
      <pdp-section-size-chart v-if="showSizeChart" @open="openSizeChart" />
    </template>

    <template #size-advice>
      <p v-if="$feature.showSizeAdvice && sizeAdvice">
        {{ sizeAdvice }}
      </p>
    </template>

    <template #skeletons>
      <div :class="brandClasses.skeletonWrapper">
        <vf-breadcrumbs-skeleton
          v-if="!hideBreadcrumbs"
          v-style:display="{
            sm: $feature.breadcrumbVisibilityOnPDP?.sm ? 'flex' : 'none',
            md: $feature.breadcrumbVisibilityOnPDP?.md ? 'flex' : 'none',
            lg: $feature.breadcrumbVisibilityOnPDP?.lg ? 'flex' : 'none',
          }"
          class="mb-4 ml-2 mt-6 lg:mt-7 md:mt-8"
        />
        <pdp-skeleton class="mb-8" />
      </div>
    </template>

    <template #bundle-upsells>
      <pdp-section-bundled-upsells
        v-if="$feature.showBundleUpsellsOnPdp && !hideUpsells?.bundle"
      />
    </template>

    <template #sticky-cta-bar>
      <client-only>
        <teleport to="body">
          <transition name="slide-bottom">
            <pdp-section-sticky-cta-bar
              v-if="showStickyCtaBar"
            />
          </transition>
        </teleport>
      </client-only>
    </template>

    <template #upsells>
      <pdp-section-upsells
        v-if="$feature.showUpsellsOnPdp && !hideUpsells?.pdp"
        :class="brandClasses.upsellsWrapper"
        :variant="config.pages.pdp.upsells?.variant"
      />
    </template>

    <template #variant-selection>
      <pdp-section-variant-selection v-if="!isDigitalProduct" />
    </template>

    <dialog-shop-the-look :class-content="brandClasses.shopTheLookDialog" />
    <dialog-pickup-in-store :hide-backdrop="enableReserveDialog" size="md" />
    <dialog-pickup-in-store-reserve
      v-if="enableReserveDialog"
      :image-src="productReserveImage"
      :product-title="product?.name"
      :selected-attributes="attributeSelectionLabels"
      :variant-id="selectedVariant?.id"
    />
    <dialog-size-chart :class-content="brandClasses.sizeChartDialog" />
    <dialog-review-write />
    <dialog-quickshop class-content="<lg:pb-0" :size="$viewport.lg ? 'lg' : undefined" />
    <dialog-variant-selection />
    <dialog-barcode />
    <dialog-notify-me />
    <dialog-terms-acceptance
      class-content="lg:w-1/3 py-2 px-4"
      :logo="logos.applePay"
      @initiate-apple-pay="handleApplePay"
    />

    <client-only v-if="isAttributesDebugEnabled">
      <lazy-app-product-content-attributes-debugger />
    </client-only>
  </pdp-layout>
</template>

<script lang="ts" setup>
import type { Props as QuickshopProps } from '#commerce/components/dialog/quickshop/Quickshop.vue'
import type { Props as VariantSelectionProps } from '#commerce/components/dialog/variant-selection/VariantSelection.vue'
import type { BaseForm as BaseFormType, PdpSectionShippingMethods as PdpSectionShippingMethodsType } from '#components'
import type { ContentProductEspots } from '#root/api/clients/content/data-contracts'
import type { DetailsData, Product } from '#root/api/clients/product/data-contracts'
import type { LocatorSearchItem } from '#types/brandify'
import type { GiftOption } from '#types/cart'
import type { Props as PickupInStoreProps } from '#types/components/product/pickupInStore'
import type { ProductQuestionsParams, ProductReviewsData, ProductReviewsParams } from '#types/components/product/reviews'
import type { InteractionOrigin, ProductContext } from '#types/gtm'
import type { GalleryEspotItem, ImageItem, SketchfabItem, VideoItem } from '#types/media'
import type { BaseNotification } from '#types/notification'
import type { Section } from '#types/page'
import type {
  AttributeSelection,
  BundleSelectionItem,
  ColorOption,
  FaqProductMetaTag,
  GiftCardFormFields,
  PriceFull,
  ProductAttributeSelectionLabels,
  ProductExtended,
  SelectedVariant,
  StringProductMetaTag
} from '#types/product'
import { PdpContextKey } from '#commerce/composables/usePdp/context'
import { ApiErrorCode } from '#root/enums/api'

definePageMeta({
  /**
   * The key is what decides if the page will be re-rendered.
   * Whenever are switching swatches we are storing the original product id on the history state
   * If a route call doesn't contain it, like a recommendation carousel, it re-renders the page
   */
  key: (route) => window?.history?.state?.pageKey || getUrlId(route.path, 'Product'),
  validate: (route) => PRODUCT_URL_REGEX.test(route.path)
})

const { seoData, ...config } = useAppConfig()
const { skipInventoryCheck, skipReviewQuestion } = config.api.brandify
const { cta: { notifyMe: notifyMeConfig }, defaultImageUrl } = config.components.product
const { allowRemovalUndo } = config.components.favorites
const { alwaysUseGrid } = config.components.vf.mediaGallery
const { showLoyaltyToastOnlyOnPDP } = config.components.vf.loyalty
const {
  allowPreselectShoeSize,
  brandClasses,
  enableColorAsAttribute,
  enableVariantSelectionDialog,
  gallery: galleryConfig,
  eSpotsWithPageContext,
  hideBreadcrumbs,
  klarnaPaymentsInline,
  questions: questionsConfig,
  quickshop: quickshopConfig,
  reviews: reviewsConfig,
  seoMetaImage,
  shopTheLook: shopTheLookConfig,
  showOutOfStockStickyCta,
  sizeChart: sizeChartConfig,
  variantSelection: variantSelectionConfig,
} = config.pages.pdp
const loyaltyLogo = config.checkout.loyalty.logo
const { logos } = config.assets.payment
const { applePayButton } = config.components.checkout
const { enableReserveDialog } = useAppConfig().components.dialog.pickupInStore
// App configs used inside computed function leak memory
// This clones the config to break the reactivity and fix the leak
// A better solution is probably available by looking deeper into the app configs
const gallery = JSON.parse(JSON.stringify(galleryConfig))

const { brand } = useRuntimeConfig().public
const { selectedStore } = storeToRefs(useBopisStore())
const cart = useCartStore()
const { allowApplePayOrderOnPDP, allowApplePayOrderOnPDPWithBopis } = useExpressPaymentFeatures()
const favorites = useFavoritesStore()
const isFavoritePending = ref(false)
const { $feature, $gtm, $locale, $seo, $t, $viewport, $optimizely } = useNuxtApp()
const { products } = useApi()
const host = useHost()
const route = useRoute()
const router = useRouter()
const toast = useToaster()
const auth = useAuthStore()
const user = useUserStore()
const loyalty = useLoyaltyStore()
const header = useHeaderStore()
const { injectFitFinder } = useFitFinder()
const { getUserEventData } = useAnalytics()
const { getIsComingSoonProduct } = useComingSoon()
const productCtaRef = useState<HTMLElement>('productCtaRef')
const reservedVariantIds = useState('pdp-reserved-variant-ids', () => new Set<string>())
const defaultStore = useLocalStorage('defaultStore', {} as LocatorSearchItem)
const isProductCtaVisible = useElementVisibility(productCtaRef)
const hideUpsells = useOptimizelyFlag('hide_upsells')
const { showApplePayTermsDialog } = config.pages.checkout
const gtmTrace = useSessionStorage('gtmTrace', {} as Record<string, { reviewsPageId?: string }>)
const {
  DialogSignIn,
  DialogPickupInStore,
  DialogPickupInStoreReserve,
  DialogLoyaltySignUp,
  DialogTermsAcceptance
} = useDialogsStore()
const DialogBarcode = createDialog('barcode')
const DialogNotifyMe = createDialog('notify-me', notifyMeConfig?.dialogOptions)
const DialogQuickshop = createDialog('quickshop', quickshopConfig?.dialogOptions)
const DialogReviewWrite = createDialog('review-write', reviewsConfig.dialogOptions.reviewWrite)
const DialogShopTheLook = createDialog('shop-the-look', shopTheLookConfig?.dialogOptions)
const DialogSizeChart = createDialog('size-chart', sizeChartConfig?.dialogOptions)
const DialogVariantSelection = createDialog('variant-selection', variantSelectionConfig?.dialogOptions)

const formRef = ref<InstanceType<typeof BaseFormType> | null>(null)
const shippingMethodsRef = ref<InstanceType<typeof PdpSectionShippingMethodsType> | null>(null)
const error = ref(false)
const addToCartLoading = ref(false)
const seoMetaHead = ref()
const shippingMethod = computed<'standard' | 'pickup' | undefined>(() => shippingMethodsRef.value?.method)
const productId = computed(() => getUrlId(route.path, 'Product'))
const reviewsQuery = reactive<ProductReviewsParams>({
  'filters': undefined,
  'paging.from': 0,
  'paging.size': reviewsConfig.pageSize,
  'q': undefined,
  'sort': reviewsConfig.previewSort ?? 'Newest',
  // get all reviews across locales based on rules in LD -
  // for example, fr-ca will display en-ca + en-us reviews
  'getAll': $feature.pdpReviewTranslate
})
const questionsQuery = reactive<ProductQuestionsParams>({
  pagingFrom: 0,
  pagingSize: questionsConfig.pageSize,
  sort: 'Newest'
})
const selectedVariantWithUpsell = ref<string[]>([])
const dialogNotification = reactive<BaseNotification>({
  message: '',
  type: 'success'
})

const { getSections, isAttributesDebugEnabled } = useCms()

const sections = ref<ReturnType<typeof getSections.product>>()
const eSpots = computed<ContentProductEspots['sectionsMap'] | undefined>(() => sections.value?.data.value?.content.sectionsMap)
const hasVisibleBelowSizesEspot = computed(() => {
  const items = (eSpots.value?.['below-sizes'] as unknown as Section | undefined)?.items || []

  return items.some(({ hideOnBreakpoint }) =>
    !hideOnBreakpoint?.sm || !hideOnBreakpoint?.md || !hideOnBreakpoint?.lg)
})
if ($feature.enablePDPESpots)
  sections.value = getSections.product(productId)

const attributeSelection = reactive<AttributeSelection>({})
const giftCardForm = ref<GiftCardFormFields>({
  amount: '',
  email: '',
  fullName: '',
  message: '',
  policy: false,
  size: ''
})
const interactionOrigin = getInteractionOrigin()

const {
  product,
  productError,
  productPending,
  inventory,
  inventoryError,
  inventoryPending
} = await useProduct(productId, {
  selectedColorId: () => typeof route.query.color === 'string' ? route.query.color : undefined,
  enableColorAsAttribute
})

const { bundlePromo, bundleUpsells } = usePromotionPlan(productId)

watch(productError, (error) => {
  if (!error) return
  throw createError({
    statusCode: error.statusCode! >= 500 ? 500 : 404,
    fatal: true
  })
}, {
  immediate: true
})

const attributeSelectionLabels = useAttributeSelectionLabels(product, attributeSelection)
const productColor = useProductColor(product)
const isFranchisePdp = computed(() => {
  const colorAttribute = product.value?.attributes.find(({ type }) => type === 'color')

  return product.value?.enableFranchisePDP === true
    && colorAttribute?.groupBy === 'productSubGroup'
    && colorAttribute.groupedOptions.length > 0
    && colorAttribute.groupedOptions.every(({ groupValue }) => isString(groupValue) && groupValue.length > 0)
})
const previewColor = shallowRef<ColorOption | null>(null)
const setColorPreview = (option: ColorOption) => {
  if (isFranchisePdp.value)
    previewColor.value = option
}
const clearColorPreview = () => {
  previewColor.value = null
}
const displayProduct = computed(() => product.value
  ? {
      ...product.value,
      badge: previewColor.value?.badge ?? product.value.badge,
      eyebrow: previewColor.value?.eyebrow ?? product.value.eyebrow,
      rating: previewColor.value?.rating
        ? { ...product.value.rating, ...previewColor.value.rating }
        : product.value.rating,
      shortDescription: previewColor.value?.description ?? product.value.shortDescription
    }
  : null
)
const { defineUser, trackEvent } = useOptimizely()
const {
  getAttrOptionAvailable,
  getAttrOptionNotifyMe,
  getMatchingVariant
} = getProductAvailability(attributeSelection, product)

/**
 * is used when open product in new tab from plp/srp
 */
const getStoredHistoryState = (key: string) => {
  const state = localStorage.getItem(key)
  localStorage.removeItem(key)
  return state ? JSON.parse(state) : {}
}

const getHistoryState = (id: string) => history.length === 1 ? getStoredHistoryState(`from_plp_${id}`) : history.state

// Map breadcrumbs - to review if we can match component and api types for breadcrumbs.
const breadcrumbs = computed(() => product.value?.breadcrumbs || [])
const productCategoryMeta = computed(() => product.value?.categoryMetaTags || {})

const isNonLoyaltyMember = computed(() => !!(auth.loggedIn && !user.isLoyaltyMember))

const setCategoryMetatags = () => {
  const categoryLevels = productCategoryMeta.value
  if (Object.keys(categoryLevels).length === 0) return
  const categoryMetaTags = Object.values(categoryLevels).reduce((acc, category, i) => {
    acc[`product_category_lv${i + 1}`] = category.trim().replace(/\s+/g, '-').toLowerCase()
    return acc
  }, {})
  seoMetaHead.value = useSeoMeta(categoryMetaTags)
}

// avoid calling on CSR as it's invoked in onMounted for that scenario
if (isServer()) setCategoryMetatags()

const pageMetaTags = computed(() => product.value?.pageMetaTags
  ?.filter(isStringProductMetaTag)
  .reduce<Record<string, StringProductMetaTag>>((acc, val) => ({
    ...acc,
    [val.id]: val
  }), {}) || {})

const faq = computed<FaqProductMetaTag['content'] | undefined>(() => getProductMetaTagFaqValue(product.value?.pageMetaTags?.find(({ id }) => id === 'faq')))

const seoPlaceholderValues = () => ({
  brand: $t.brand,
  brandName: $t.brandName,
  productName: product.value?.name || ''
})

const fallbackTitle = computed(() => product.value && replaceAll($seo.pdp.title, seoPlaceholderValues()))
const fallbackDescription = computed(() => product.value && replaceAll($seo.pdp.description, seoPlaceholderValues()))

useHead(() => {
  const { gallery: produtGallery, url } = product.value || {}
  const [canonicalUrl] = `https://${host}${product.value?.url}`.split('?')

  return {
    titleTemplate: pageMetaTags.value.title?.content || fallbackTitle.value,
    link: [{ rel: 'canonical', href: canonicalUrl }],
    meta: [
      {
        name: 'description',
        content: pageMetaTags.value.description?.content || fallbackDescription.value
      },
      {
        name: 'og:title',
        content: pageMetaTags.value['og:title']?.content || titleTemplate($t, fallbackTitle.value)
      },
      {
        name: 'og:description',
        content: pageMetaTags.value['og:description']?.content || fallbackDescription.value
      },
      {
        name: 'og:image',
        content: produtGallery?.[0].src || seoMetaImage
      },
      {
        name: 'og:type',
        content: 'website'
      },
      {
        name: 'og:url',
        content: `https://${host}${url}`
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image'
      },
      {
        name: 'twitter:creator',
        content: `@${brand}`
      },
      {
        name: 'twitter:site',
        content: `@${brand}`
      },
      {
        name: 'robots',
        content: pageMetaTags.value.robots?.content || 'index,follow'
      },
    ]
  }
})

let reviewsData = ref<ProductReviewsData>()
let reviewsError = ref()
let reviewsPending = ref(false)

if ($feature.showReviewsOnPDP) {
  // The best approach here would be to use a non-immediate API request and execute it if the flag is enabled,
  // similar to the way Q&A is handled below. Unfortunately, it turns out that in some cases, SSR requests
  // get resolved before all updates associated with non-immediate API calls are run, causing the
  // JSON LD data for reviews to be missing on an initial page load, so we have to fall back to using an immediate call.
  ({ pending: reviewsPending, data: reviewsData, error: reviewsError } = products.reviews(productId, reviewsQuery, {
    lazy: true,
    transform: (data): ProductReviewsData => ({
      paging: data.paging,
      reviews: $feature.pdpReviewTranslate
        ? data.results[0].reviews.map((review) =>
            // Remove media only for NORA reviews that are shared to EMEA
            ['en_US', 'fr_CA'].includes(review.details?.locale)
              ? { ...review, media: [] }
              : review
          )
        : data.results[0].reviews,
      rollup: reviewsQuery['paging.from'] === 0 ? data.results[0].rollup : reviewsData.value?.rollup,
      pageId: data.results[0].productId,
      reviewsPageId: data.reviewsPageId
    })
  }))
}

const reviewsSummaryData = computed<ProductReviewsData | undefined>(() => {
  const data = reviewsData.value
  const rating = previewColor.value?.rating
  if (!data?.rollup || !rating) return data

  return {
    ...data,
    rollup: {
      ...data.rollup,
      averageRating: rating.score ?? data.rollup.averageRating,
      reviewCount: rating.reviewsCount ?? data.rollup.reviewCount
    }
  }
})

const translations = useI18n()

useSchemaOrg(() =>
  product.value && reviewsData.value
    ? [
        getJsonLdForProductGroup(
          product.value,
          reviewsData.value,
          host,
          translations.value.urls,
          enableColorAsAttribute
        ),
        getJsonLdForProduct(product.value, reviewsData.value, seoData, host, brand, $locale, translations.value.urls),
        getJsonLdForBreadcrumbs(product.value, breadcrumbs.value, host),
        getJsonLdForShippingDetails(host, translations.value.urls, $locale),
        getJsonLdForReturnPolicy(host, translations.value.urls, seoData[$locale]?.returnPolicyJsonLd),
        ...(faq.value ? [getJsonLdForProductFaq(product.value, faq.value, host)] : []),
      ]
    : []
)

const {
  status: questionsStatus,
  data: questionsData,
  error: questionsError,
  execute: questionsExecute
} = products.questions(productId, questionsQuery, {
  lazy: true,
  immediate: false,
  ...((skipReviewQuestion?.includes($locale) || !$feature.showReviewQAndAOnPDP) ? { watch: false } : '')
})

const questionsPending = computed(() => questionsStatus.value === 'pending')

const eyebrowStyle = computed(() => {
  const colorHexCode = product?.value?.eyebrow?.colorHexCode
  return colorHexCode && colorHexCode !== 'None' ? { style: `color: #${colorHexCode}` } : {}
})

if ($feature.showReviewQAndAOnPDP) questionsExecute()

if (auth.loggedIn && allowPreselectShoeSize && !user.profile) user.get()

const variantRequestPending = computed(() => productPending.value || inventoryPending.value)
const pending = computed(() => variantRequestPending.value || user.pending)

const isAttributeSelectionRequired = computed(
  () => Object.keys(attributeSelection).length >= (enableColorAsAttribute ? 2 : 1)
)
const isVariantSelected = computed(() =>
  isAttributeSelectionRequired.value
  && product.value?.attributes.every(({ type }) => attributeSelection[type] || type === 'color')
)
const nextAttribute = computed(() => {
  const index = Object.entries(attributeSelectionLabels.value).findIndex(
    ([type, { value }]) => (enableColorAsAttribute || type !== 'color') && !value
  )
  return product.value?.attributes[index]
})
const selectedVariant = computed(() => {
  const variant = product.value?.variants.find((item) =>
    Object.entries(attributeSelection).every(([key, value]) => !item.attributes[key] || item.attributes[key] === value || key === 'color')
  )
  return (variant && isVariantSelected.value ? variant : null) as SelectedVariant
})
const defaultVariant = computed(() => selectedVariant.value || product.value?.variants.find(({ buyable }) => buyable))
const isCurrentVariantReserved = computed(() =>
  !!selectedVariant.value?.id && reservedVariantIds.value.has(selectedVariant.value.id)
)
const isDigitalProduct = computed(() => product.value?.productType?.toLowerCase() === 'digital')

const productPrice = computed<PriceFull | undefined>(() => {
  if (previewColor.value?.price)
    return previewColor.value.price

  const customAmount = Number(giftCardForm.value.amount)
  if (isDigitalProduct.value && giftCardForm.value.amount && Number.isFinite(customAmount)) {
    return {
      highCurrent: customAmount,
      lowCurrent: customAmount,
      highOriginal: customAmount,
      lowOriginal: customAmount,
    }
  }
  else if (selectedVariant.value?.maxDigitalProductAmount) {
    return {
      highCurrent: selectedVariant.value.maxDigitalProductAmount,
      lowCurrent: selectedVariant.value.minDigitalProductAmount || 0,
      highOriginal: selectedVariant.value.maxDigitalProductAmount,
      lowOriginal: selectedVariant.value.minDigitalProductAmount || 0,
    }
  }
  else if (selectedVariant.value?.price) {
    return {
      highCurrent: selectedVariant.value.price.current ?? selectedVariant.value.price.original,
      lowCurrent: selectedVariant.value.price.current ?? selectedVariant.value.price.original,
      highOriginal: selectedVariant.value.price.original,
      lowOriginal: selectedVariant.value.price.original,
      historicBestPrice: selectedVariant.value.price.historicBestPrice,
      percentagePriceDiscount: selectedVariant.value.price.percentagePriceDiscount,
      previousBestSalesPrice: selectedVariant.value.price.previousBestSalesPrice,
      salesPriceActiveDate: selectedVariant.value.price.salesPriceActiveDate
    }
  }
  return product.value?.price
})

// TODO: address whether all of these computeds are needed both here and within the product-cta component
const isAnyVariantNotifyMe = computed(() => product.value?.variants.some(({ notifyMe }) => !!notifyMe))
const isSelectedVariantOutOfStock = computed(() =>
  // Out of Stock means we have a variant whose inventory state is not equal to 'InStock'
  isVariantSelected.value && selectedVariant.value?.productInventoryState !== 'InStock'
)

const isComingSoon = computed(
  () => $feature.showComingSoonOnPDP && getIsComingSoonProduct(product.value?.badge, product.value?.eyebrow)
)
const isNotifyMe = computed(
  () => $feature.showNotifyMeOnPDP
    && !inventoryPending.value
    && (selectedVariant.value?.notifyMe || (isAnyVariantNotifyMe.value && !pending.value && !isVariantSelected.value))
)
const isOutOfStock = computed(
  () => !product.value || isSelectedVariantOutOfStock.value || product.value?.allVariantsOutOfStock
)
const stickyCtaDisplay = shallowRef<{
  product: ProductExtended | null
  attributeSelection: AttributeSelection
}>({
  product: null,
  attributeSelection: {}
})

watchEffect(() => {
  if (variantRequestPending.value) return

  stickyCtaDisplay.value = {
    product: !isDigitalProduct.value && (!isComingSoon.value || isNotifyMe.value)
      ? displayProduct.value
      : null,
    attributeSelection: { ...attributeSelection }
  }
})

const showStickyCtaBar = computed(() =>
  !isProductCtaVisible.value
  && !!stickyCtaDisplay.value.product
  && (!isOutOfStock.value || showOutOfStockStickyCta)
)
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && product.value?.isRobisOnly)
const isSignInToBuy = computed(() => !!product.value?.presale && (!auth.loggedIn || isNonLoyaltyMember.value))

const isBOPISEnabled = computed(
  () => !(
    isSignInToBuy.value
    || isComingSoon.value
    || (isNotifyMe.value && isOutOfStock.value)
    || product.value?.allVariantsOutOfStock
    || isCustomsProduct(product)
  )
)

const fitRating = computed(() => {
  const histogramProperties = reviewsData.value?.rollup?.properties
    ?.filter(({ display_type }) => display_type === 'histogram')

  return histogramProperties?.[histogramProperties.length - 1]
})

const modelMeasurements = computed(() => {
  const modelMeasurements = product.value?.modelMeasurements
  if (!modelMeasurements?.length) return
  if (modelMeasurements[0].includes('|'))
    return [...modelMeasurements[0].split('|'), ...modelMeasurements.slice(1)]
  return modelMeasurements
})

const productReserveImage = computed(() => product.value?.gallery?.find(({ type }) => type === 'image')?.src)

// Gender gallery context carried from the originating PLP via history state,
// so the assigned men/women imagery persists into the PDP and through swatch changes.
const galleryContext = ref<string | undefined>(
  import.meta.client ? window.history.state?.galleryContext : undefined
)

const gallerySource = computed(() => ({
  id: product.value?.id,
  gallery: productColor.value?.gallery || product.value?.gallery,
  altGalleries: productColor.value?.altGalleries || product.value?.altGalleries
}))

const sizeAdvice = computed(() => {
  const key = product.value?.sizeAdvice
  return key ? $t.sizeAdvice[key] : ''
})

// optimizely A/B test for the media gallery. `activeGallery` is the media
// array for the user's assigned variant (falling back to the primary gallery),
// awaits its client-side decision to avoid image flashing / content shift.
const { activeGallery, galleryPending, isGenderedGallery } = useActiveGallery(gallerySource, galleryContext)

const getEspotInsertPosition = ({ position }: Record<string, unknown>, mediaLength: number) => {
  const resolvedPosition = getValueForBreakpoint($viewport.breakpoint, position)
  if (typeof resolvedPosition !== 'number' || !Number.isInteger(resolvedPosition)) return mediaLength

  return Math.min(Math.max(0, resolvedPosition - 1), mediaLength)
}

const galleryItems = computed<(ImageItem | VideoItem | SketchfabItem | GalleryEspotItem)[]>(() => {
  const { name } = product.value || {} as Partial<DetailsData>
  const productGallery = previewColor.value?.gallery?.length
    ? previewColor.value.gallery
    : activeGallery.value

  const mediaItems: (ImageItem | VideoItem | SketchfabItem | GalleryEspotItem)[] = productGallery.map((item) =>
    (item.type === 'image' && gallery?.presets)
      ? {
          ...item,
          src: getImageTransformations(item.src, {
            ...gallery.presets[
              alwaysUseGrid || productGallery.length > 1
                ? 'src'
                : 'srcZoom'],
            assetTransformation: item.assetTransformation
          }),
          srcLarge: getImageTransformations(item.src, {
            ...gallery.presets.srcLarge,
            assetTransformation: item.assetTransformation
          }),
          srcZoom: getImageTransformations(item.src, {
            ...gallery.presets.srcZoom,
            assetTransformation: item.assetTransformation
          }),
          alt: item.alt || name
        }
      : item.type === 'video'
        ? {
            ...item,
            src: getVideoTransformations(item.src, $feature.configVideoPresets),
            width: gallery.presets.src.width,
            height: gallery.presets.src.height,
            poster: getImageTransformations(item.meta.poster, {
              crop: 'fill',
              width: gallery.presets.src.width,
              height: gallery.presets.src.height
            }),
            autoplay: item.meta?.autoplay,
            loop: item.meta?.autoplay
          }
        : item
  )

  const gallerySection = eSpots.value?.['pdp-media-gallery']
  if (!gallerySection?.items?.length) return mediaItems

  const merged = [...mediaItems]
  gallerySection.items.forEach((espotItem) => {
    if (!isObject(espotItem) || espotItem.type !== 'VfCanvasPDPGenericEspot' || !espotItem.component) return

    const insertAt = getEspotInsertPosition(espotItem, merged.length)

    merged.splice(insertAt, 0, {
      type: 'espot',
      id: typeof espotItem.id === 'string' ? espotItem.id : undefined,
      section: {
        name: `${gallerySection.name}-${espotItem.id}`,
        lazy: gallerySection.lazy,
        items: [{ ...espotItem.component, roundedCornerPreset: 'default' } as Section['items'][number]]
      }
    })
  })

  return attachGalleryHotspots(merged, gallerySection.items)
})

const fetchCustomsData = async (customsRecipeID, size, locale) => {
  try {
    const [{ sku }, imageUrl] = await Promise.all([
      $fetch(`/fapi/customs-sku/${customsRecipeID}`, { query: { locale, size } }),
      $fetch(`/fapi/customs-image/${customsRecipeID}`, {
        query: { locale },
        default: () => defaultImageUrl
      })
    ])
    return { productId: sku, customsProductImageURL: imageUrl }
  }
  catch {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong,
        message: $t.itemCannotAddedToCart,
      }
    })
    return { productId: undefined, customsProductImageURL: undefined }
  }
}

// staged bundle-upsell picks, saved from the 'bundle-selection' Quickshop context but not yet added to cart
const bundleSelection = ref<BundleSelectionItem[]>([])
// current PDP product (1) + every staged bundle pick, shown on the main Add to Cart button
const bundleItemCount = computed(() => 1 + bundleSelection.value.length)
const bundleAddToCartLabel = computed(
  () => bundleItemCount.value > 1 ? replaceAll($t.addToCartCount, { count: bundleItemCount.value }) : undefined
)

// keyed by product id (not upc) - editing a selection to a different variant/upc replaces the same entry
const upsertBundleSelection = (selection: BundleSelectionItem) => {
  const index = bundleSelection.value.findIndex(({ product }) => product.id === selection.product.id)
  if (index === -1) bundleSelection.value.push(selection)
  else bundleSelection.value[index] = selection
}

const removeBundleSelection = (id: string) => {
  bundleSelection.value = bundleSelection.value.filter(({ product }) => product.id !== id)
}

// Add to cart
const addToCart = async (
  {
    suppressMiniCart,
    extendedGtmConfig,
    handleValidation = true,
    giftOption,
    customAmount
  }: {
    suppressMiniCart?: boolean
    extendedGtmConfig?: ProductContext & { appSource?: InteractionOrigin }
    handleValidation?: boolean
    giftOption?: GiftOption
    customAmount?: { amount?: number }
  } = {}
): Promise<boolean> => {
  if (variantRequestPending.value) return false

  if (isSignInToBuy.value) {
    if (isNonLoyaltyMember.value)
      await DialogLoyaltySignUp.open({ formLocation: 'modal:single:myaccount:none', isSignInToBuy: true })
    else
      await DialogSignIn.open({ formLocation: 'modal:single:pdp:none', isSignInToBuy: true })

    if (auth.loggedIn && !isNonLoyaltyMember.value) {
      await until(cart.pending).toBe(false)
      addToCart()
    }
    return true
  }

  if (handleValidation) {
    error.value = !formRef.value?.validate(true)
  }

  if (error.value) return error.value

  if (!selectedVariant.value) {
    toast.add({
      props: {
        type: 'error',
        message: $t.apiMessages.GEN500
      }
    })
  }

  addToCartLoading.value = true
  const upc = selectedVariant.value?.upc
  const storeId = shippingMethod.value === 'pickup' ? selectedStore.value?.storeid : undefined

  const { productId, customsProductImageURL } = product.value?.customsRecipeID && product.value?.dummyCustoms
    ? await fetchCustomsData(product.value.customsRecipeID, selectedVariant.value?.attributes.size, $locale)
    : { productId: selectedVariant.value?.id, customsProductImageURL: undefined }

  if (!productId) {
    addToCartLoading.value = false
    return false
  }

  if (cart.selectedUpsells.length >= 1)
    selectedVariantWithUpsell.value.push(productId, ...cart.selectedUpsells)

  if (productId) {
    const state = getHistoryState(product.value!.id)

    setGtmCartProductsMap(productId, product.value)
    if (state?.category) setGtmCartProductVariant(productId, { category: state.category })
    if (extendedGtmConfig?.appSource) setGtmCartProductVariant(productId, extendedGtmConfig)
  }

  if (storeId) cart.favoriteStoreId = storeId

  gtmTrace.value[productId] = {
    ...gtmTrace.value[productId],
    reviewsPageId: reviewsData.value?.reviewsPageId
  }
  const activeImage = activeGallery.value.find(({ type }) => type === 'image')

  // staged bundle-upsell picks ride along in the same cart.add() call, so the backend promotion
  // engine sees the full combination and can apply the bundle discount
  const bundleItemsToAdd = bundleSelection.value.map(({ productId, qty, upc }) => ({
    productId,
    qty,
    upc
  }))

  try {
    await withRetryAfterTime(() => cart.add([
      {
        productId,
        upc,
        maxQty: inventory.value?.variants[productId]?.quantity ?? 9999999,
        precreatedCustomsCode: product.value?.dummyCustoms ? product.value.id : undefined,
        productImageURL: customsProductImageURL ?? activeImage?.thumbnail ?? activeImage?.src,
        dummyCustomsPdpUrl: product.value?.dummyCustoms ? window.location.href : undefined
      },
      ...bundleItemsToAdd
    ], {
      suppressMiniCart: isBoolean(suppressMiniCart) ? suppressMiniCart : undefined,
      giftOption,
      customAmount,
      extendedGtmConfig,
      storeId,
      recipeId: isCustomsProduct(product) ? product.value?.customsRecipeID : undefined,
    }), 2000, [ApiErrorCode.GWP_PRODUCT])
  }
  catch (err) {
    log.error(err)
  }

  if (cart.errorMessage) {
    toast.add({
      props: {
        type: 'error',
        message: cart.errorMessage
      }
    })
    if (productId && product.value) removeGtmCartProductsMap(productId, product.value?.id)
  }
  else {
    bundleSelection.value = []
  }

  selectedVariantWithUpsell.value = []
  addToCartLoading.value = false

  return !!cart.errorMessage
}

const setNotification = ({ message, type }: BaseNotification) => {
  dialogNotification.message = message
  dialogNotification.type = type

  if (message) setTimeout(scrollToElement, 250, 'pdpContainer')
}
const resetNotification = () => setNotification({ message: '', type: 'success' })

const findInStore = async (
  attributeSelectionLabels: ProductAttributeSelectionLabels,
  mode: PickupInStoreProps['mode'] = 'find'
) => {
  error.value = !formRef.value?.validate(true)

  if (error.value) return

  $gtm.push('pdpPage.onFindInStore')

  const productId = selectedVariant.value?.id

  if (productId) {
    await DialogPickupInStore.open({
      item: {
        ...product.value!,
        productId
      },
      selectedAttributes: attributeSelectionLabels,
      mode,
      skipInventoryCheck: skipInventoryCheck?.includes($locale)
    })

    closeDialogs()
  }
}

const triggerApplePaySession = () => {
  // check for error in the product selection
  error.value = !formRef.value?.validate(true)

  if (!error.value) {
    const getPayload = (selectedVariantSku, selectedUpsellsValue) => {
      if (selectedUpsellsValue.length >= 1)
        return [...selectedVariantWithUpsell.value, selectedVariantSku]
      else
        return selectedVariantSku
    }

    const selectedVariantSku = selectedVariant.value?.id || ''
    const productId = getPayload(selectedVariantSku, cart.selectedUpsells)
    const { storeId, shippingContact } = (shippingMethod.value === 'pickup' && selectedStore.value?.storeid)
      ? {
          storeId: selectedStore.value?.storeid,
          shippingContact: {
            addressLines: [
              selectedStore.value?.address1,
              selectedStore.value?.address2
            ].filter((value): value is string => !!value),
            locality: selectedStore.value?.city || '',
            administrativeArea: selectedStore.value?.province || selectedStore.value?.state || selectedStore.value?.country || '',
            countryCode: selectedStore.value?.country || '',
            postalCode: selectedStore.value?.postalcode || ''
          }
        }
      : { storeId: undefined, shippingContact: undefined }
    const payload = { productId, upc: selectedVariant.value?.upc, storeId, shippingContact }

    $gtm.push('checkout.onLoadEventData', 'APPLE_PAY')

    // initiate the temporary applePay only after clicking the button
    const { initApplePaySession } = useApplePay('applePayPdp')
    initApplePaySession(payload)

    if (cart.errorMessage) {
      toast.add({
        props: {
          type: 'error',
          title: $t.somethingWentWrong,
          message: cart.errorMessage
        }
      })
    }
  }
}

// Favorites
const isFavorite = computed(() => favorites.isFavorite(
  ($feature.enableVariantFavorites ? selectedVariant.value?.id : product.value?.id) || ''
))

const addFavorite = async () => {
  error.value = !!($feature.enableVariantFavorites && !formRef.value?.validate(true))
  const { id } = $feature.enableVariantFavorites ? selectedVariant.value : product.value || {}

  if (error.value || !id) return

  isFavoritePending.value = true
  const addFavoriteResult = await favorites.add(id)
  isFavoritePending.value = false

  if (addFavoriteResult.error) {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong
      }
    })
  }
  else if (addFavoriteResult?.limitReached) {
    toast.add({
      id: 'favorites-limit-reached',
      props: {
        type: 'info',
        message: $t.favoritesLimitReached,
        ctas: [{
          label: $t.viewFavorites,
          to: '/favorites'
        }]
      }
    })
  }
  else {
    toast.add({
      props: {
        type: 'success',
        message: replaceAll($t.favoritesAdded, { name: product.value?.name }),
        ctas: [{
          label: $t.viewFavorites,
          to: '/favorites'
        }]
      }
    })
    $gtm.push('pdpPage.onAddToFavorites', `${productId.value} - ${productColor.value?.label}`)
  }
}

const undoRemoveFavorite = async (id: string, name?: string) => {
  const { error: addFavoriteError } = await favorites.add(id)

  if (addFavoriteError) {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong
      }
    })
  }
  else {
    toast.remove('favorite-removed')
    toast.add({
      id: 'favorite-added',
      props: {
        type: 'success',
        message: replaceAll($t.favoritesAdded, { name }),
        ctas: [{
          label: $t.viewFavorites,
          to: '/favorites'
        }]
      }
    })
  }
}

const removeFavorite = async () => {
  error.value = !!($feature.enableVariantFavorites && !formRef.value?.validate(true))
  const { id } = $feature.enableVariantFavorites ? selectedVariant.value : product.value || {}

  if (error.value || !id) return

  isFavoritePending.value = true
  const { error: removeFavoriteError } = await favorites.remove(id)
  isFavoritePending.value = false

  if (removeFavoriteError) {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong
      }
    })
  }
  else {
    toast.remove('favorite-removed')
    toast.add({
      id: 'favorite-removed',
      props: {
        type: !allowRemovalUndo ? 'success' : '',
        ctaPosition: allowRemovalUndo ? 'right' : undefined,
        message: replaceAll($t.favoritesRemoved, { name: product.value?.name }),
        ctas: [
          ...(allowRemovalUndo
            ? [{
                label: $t.undo,
                click: () => undoRemoveFavorite(id, product.value?.name)
              }]
            : [{
                label: $t.viewFavorites,
                to: '/favorites'
              }])
        ]
      }
    })
    $gtm.push('pdpPage.onRemoveFromFavorites', `${productId.value} - ${productColor.value?.label}`)
  }
}

const isValidAttributeValue = (selection: string, options: any) => options.some(
  ({ productUrl, value }) => value === selection && (!productUrl || route.path === productUrl)
)

const selectColor = async (_id, url, colorLabel?: string) => {
  clearColorPreview()
  const _url = new URL(url, document.baseURI)
  const color = _url.searchParams.get('color') || ''
  const id = url ? getUrlId(url, 'Product') : color
  const currentQuery = Object.fromEntries((new URLSearchParams(window.location.search)).entries())
  const colorOptions = product.value?.attributes.find(({ type }) => type === 'color')?.options
  const selectedColor = colorOptions?.find(({ id, value }) => id === _id || value === _id)
  const selectedProductId = selectedColor?.id || (color ? `${id}${color}` : id)

  if (colorLabel)
    $gtm.push('pdpPage.onSelectColor', `${selectedProductId} - ${colorLabel}`)

  if (color) attributeSelection.color = color

  router.replace(
    color
      ? {
          query: { ...currentQuery, color },
          state: { ...window.history.state, skuChange: true, pageKey: window.history.state.pageKey || productId.value }
        }
      : {
          path: url,
          query: currentQuery,
          state: { ...window.history.state, skuChange: true, pageKey: window.history.state.pageKey || productId.value }
        }
  )

  // Reset the error state when navigating between colours
  // to prevent the error message from showing up when we remove an attribute selection
  // due to the selected value not being present on the colourway we're navigating to
  error.value = false
}

const selectSize = (type, value, label?: string) => {
  attributeSelection[type] = value
  const currentQuery = Object.fromEntries((new URLSearchParams(window.location.search)).entries())
  router.replace({
    query: { ...currentQuery, ...attributeSelection },
    state: { ...window.history.state, pageKey: window.history.state.pageKey || productId.value }
  })

  if (label)
    $gtm.push('pdpPage.onSelectSize', `${product.value?.id} - ${label}`)
}

const preselectAttributes = async () => {
  if (productPending.value)
    await until(productPending).toBe(false)

  if (!product.value) return

  if (!inventory.value)
    await until(inventoryPending).toBe(false)
  // "Invalid" in this context means no longer present after switching colours
  const invalidQueryTypes: string[] = []

  product.value?.attributes.forEach(({ type, options }) => {
    if (!enableColorAsAttribute && type === 'color') return

    const availableOptions = options.filter(({ available }) => available)
    const queryValue = (route.query?.[type] || '') as string
    const validQueryValue = queryValue && isValidAttributeValue(queryValue, options)

    // Remove invalid selection values from the component data
    if (attributeSelection[type] && !isValidAttributeValue(attributeSelection[type]!, options))
      delete attributeSelection[type]

    if (validQueryValue)
      attributeSelection[type] = queryValue
    else if (options?.length === 1)
      attributeSelection[type] = options[0].value
    else if ($feature.enablePreselectFirstWidth && type === 'width')
      attributeSelection[type] ??= availableOptions[0]?.value || options[0].value
    else if (allowPreselectShoeSize && type === 'size' && product.value?.productType === 'Footwear')
      attributeSelection[type] = user.preferredShoeSize
    else if (type === 'color')
      attributeSelection[type] = (options?.find(({ defaultColor }) => defaultColor) || options[0])?.value
    else if (type === 'fitType') attributeSelection[type] ??= options?.[0]?.value || options?.[0]?.label

    if (queryValue && !validQueryValue)
      invalidQueryTypes.push(type)
  })

  // Remove invalid selection values from the query string
  // Adds default selection to the query string
  const queryParams = { ...attributeSelection, ...route.query }
  router.replace({
    ...route,
    query: {
      ...Object.fromEntries(Object.entries(queryParams).filter(([key]) => !invalidQueryTypes.includes(key)))
    },
    state: { ...window.history.state, pageKey: window.history.state.pageKey || productId.value }
  })

  formRef.value?.validate()
}

const isLoyaltyToastBackdropVisible = ref(false)

const displayLoyaltyToast = () => {
  if (
    !$feature.showLoyaltyRewardsInUtilityNav
    || !showLoyaltyToastOnlyOnPDP
    || !user.details
    || !$viewport.sm
  ) {
    return
  }

  const isLoyaltyToastAlreadyDisplayed = useLocalStorage('isLoyaltyToastAlreadyDisplayed', false)
  if (isLoyaltyToastAlreadyDisplayed.value) return

  const closeLoyaltyToastDelay = 5000

  const greetingText = user.details?.firstName
    ? replaceAll($t.loyaltyRewardsDetails.greetingWithName, { name: user.details.firstName })
    : $t.loyaltyRewardsDetails.greeting
  const rewardAmountText = replaceAll($t.loyaltyRewardsDetails.pointsAmount, { amount: loyalty.currentReward })

  const bottomText = loyalty.vouchers?.expiringLoyaltyPoints !== undefined
    ? replaceAll($t.rewardPointsExpireInNextDays, {
        points: loyalty.vouchers.expiringLoyaltyPoints,
        days: loyalty.vouchers.expiringLoyaltyPointsDays,
      })
    : ''
  toast.add({
    props: {
      type: '',
      classMessage: 'ws-pre-wrap',
      message: `${greetingText}\n${rewardAmountText}`,
      bottomText,
      icon: loyaltyLogo ? { src: loyaltyLogo, size: '3xl' } : undefined,
    },
    delay: closeLoyaltyToastDelay,
  })
  isLoyaltyToastAlreadyDisplayed.value = true
  isLoyaltyToastBackdropVisible.value = true
  setTimeout(() => isLoyaltyToastBackdropVisible.value = false, closeLoyaltyToastDelay)
}

const onZoom = (index: number, zoomFactor: number) => {
  if (zoomFactor) $gtm.push('pdpPage.onClickFullImage', `${product.value?.name} | ${index + 1}`)
  $gtm.push(
    'pdpPage.onImageZoom',
    zoomFactor ? `${zoomFactor * 100}% | ${index + 1}` : `Reset Zoom | ${index + 1}`
  )
}

const openQuickshop = (
  product: Product,
  options: {
    context?: QuickshopProps['context']
    gtmConfig?: ProductContext & { appSource?: InteractionOrigin }
    initialAttributes?: QuickshopProps['initialAttributes']
    isUpsell?: QuickshopProps['isUpsell']
    variant: QuickshopProps['variant']
  }
) => DialogQuickshop.open({ product, ...options })

const openShopTheLook = (productSet) => {
  $gtm.push('shopTheLook.onClickOpenModal', productSet.productSetId, { bundleId: productSet.productSetId })
  DialogShopTheLook.open({ productSet, onOpenQuickshop: (product) => {
    if (!product) return

    DialogQuickshop.keep().open({
      product,
      gtmConfig: { appSource: 'shop-the-look', bundleId: productSet.productSetId },
      suppressMiniCart: true
    })
  } }).then((automatic) => {
    if (!automatic)
      $gtm.push('shopTheLook.onClickCloseModal', productSet.productSetId, { bundleId: productSet.productSetId })
  })
}

const showApplePay = computed(() =>
  allowApplePayOrderOnPDP.value
  && window.ApplePaySession
  && (allowApplePayOrderOnPDPWithBopis.value || shippingMethod.value !== 'pickup')
  && !isOutOfStock.value
  && !(isNotifyMe.value && isOutOfStock.value)
  && !isComingSoon.value
  && !isRobisOnly.value
)

const showFavorites = computed(() => !isDigitalProduct.value && (
  selectedVariant.value?.notifyMe && $feature.showNotifyMeOnPDP && isSelectedVariantOutOfStock.value
    ? $feature.showFavoritesWhenNotifyMe
    : $feature.catalogFavorites
))

const showFindInStore = computed(
  () => $feature.showFindInStoreOnPDP && !isDigitalProduct.value && !isComingSoon.value
)

const hasFitFinderContent = ref(false)

const showFitFinder = computed(() => $feature.showFitFinderOnPDP && !isDigitalProduct.value && !isComingSoon.value)

const showFitRating = computed(() => $feature.showFitRatingOnPDP && !isDigitalProduct.value && !isComingSoon.value)

const showModelMeasurements = computed(
  () => $feature.showModelMeasurementsOnPDP
    && !!modelMeasurements.value
    && !isGenderedGallery.value
    && !isDigitalProduct.value
    && !isComingSoon.value
)

const showReserveInStore = computed(
  () => $feature.allowProductReservationOnPDP && !isDigitalProduct.value && !isComingSoon.value && !isRobisOnly.value
)

const showShopTheLook = computed(() => {
  if (isDigitalProduct.value || !$feature.pdpShopTheLook) return false

  const productSets = product.value?.productSets
  if (!productSets?.length) return false

  const isSingleProductSet = productSets.length === 1 && productSets[0].products?.length === 1
  return !(isSingleProductSet && productSets[0].products?.[0]?.id === product.value?.id)
})

const showSizeChart = computed(
  () => $feature.showSizeChartButtonOnPDP
    && !!product.value?.sizeChart
    && !isDigitalProduct.value
    && !isComingSoon.value
)

const openSizeChart = () => {
  $gtm.push('pdpPage.onOpenSizeGuide')
  if (product.value?.sizeChart) {
    DialogSizeChart.open({
      productType: product.value.sizeChart,
      id: product.value.id,
      sizeAdvice: product.value.sizeAdvice
    })
  }
}

const handleNextAttribute = (options: Omit<VariantSelectionProps, 'attribute'> = {}, callback?: () => void) => {
  if (!nextAttribute.value) return callback?.()
  if (enableVariantSelectionDialog) {
    DialogVariantSelection.open({ attribute: nextAttribute.value, ...options }).then((payload) => {
      if (payload?.handleNextAttribute) handleNextAttribute(options, callback)
    })
  }
  else {
    error.value = true
    scrollToElement('sizePickers')
  }
}

const handleAddToCart = () => {
  if (variantRequestPending.value) return

  if (enableVariantSelectionDialog) {
    handleNextAttribute({}, addToCart)
  }
  else {
    addToCart()
  }
}

const handleSignInToBuy = async () => {
  if (isSignInToBuy.value) {
    await DialogSignIn.open({
      formLocation: 'modal:single:pdp:none',
      isSignInToBuy: true
    })
    if (auth.loggedIn) {
      await until(cart.pending).toBe(false)
      return handleSignInToBuy()
    }
    throw new Error('User not signed in')
  }
}

const handleApplePay = async () => {
  try {
    await handleSignInToBuy()
    handleNextAttribute({ enableApplePay: true }, triggerApplePaySession)
  }
  catch (err) {
    console.info(err)
  }
}

const openTermsAcceptanceDialog = () => {
  if (showApplePayTermsDialog) {
    DialogTermsAcceptance.open()
  }
  else {
    handleApplePay()
  }
}

const handleNotifyMe = () => {
  if (!product.value) return

  if (isComingSoon.value || selectedVariant.value) {
    const size = attributeSelectionLabels.value.size?.value || attributeSelection.size
    const length = attributeSelection.length
    const width = attributeSelection.width
    const gtmEventLabel = `${product.value.id} - ${productColor.value?.label}: ${[size, length, width].filter(Boolean).join('/')}`

    DialogNotifyMe.keep().open({
      gtmEventLabel,
      isComingSoon: isComingSoon.value,
      productId: isComingSoon.value ? product.value.id : selectedVariant.value.id,
      productName: product.value.name,
      selectedAttributes: attributeSelectionLabels.value,
      subscriptionType: product.value.subscriptionType,
      setNotification
    }, { depth: 'md' })

    $gtm.push('pdpPage.onNotifyMeOpen', product.value.id)
  }
  else {
    if (enableVariantSelectionDialog) {
      handleNextAttribute({}, () => handleNotifyMe())
    }
    else {
      error.value = true
      scrollToElement('sizePickers')
    }
  }
}

const handleInStore = (mode: PickupInStoreProps['mode'] = 'find') => {
  try {
    handleNextAttribute(
      { enableFindInStore: mode === 'find', enableReserveInStore: mode === 'reserve' },
      () => findInStore(attributeSelectionLabels.value, mode)
    )
  }
  catch (err) {
    console.info(err)
  }
}

const handleReserve = async () => {
  error.value = !formRef.value?.validate(true)
  if (error.value) return
  if (!selectedVariant.value || !product.value || !defaultStore.value?.storeid) return

  const item = { ...product.value, productId: selectedVariant.value.id }
  const { cartId, reserve, storeShippingAddress } = useProductReservation({ item, store: defaultStore.value })

  try {
    await reserve()
    await DialogPickupInStoreReserve.open({
      cartId: cartId.value,
      storeShippingAddress: storeShippingAddress.value,
      productTitle: product.value.name,
      selectedAttributes: attributeSelectionLabels.value,
      variantId: selectedVariant.value.id
    })
    closeDialogs()
  }
  catch (e) {
    console.info(e)
  }
}

let cachedGtmDetailContext: Pick<ProductContext, 'list' | 'category' | 'breadcrumbs' | 'searchTerm'> | undefined

const pushProductDetailViewEvent = (product) => {
  const hasUtmParam = Object.keys(getTrackingQueryParams(route.query)).length > 0
  setInteractionOrigin(hasUtmParam ? 'direct-to-pdp' : interactionOrigin)
  const variant = selectedVariant.value || defaultVariant.value

  if (!cachedGtmDetailContext) {
    const state = getHistoryState(product.id)
    const breadcrumbs = deserializeAnalyticsBreadcrumbs(state.breadcrumbs)
    const searchTerm = state.searchTerm ?? state.suggestedPhrase
    cachedGtmDetailContext = {
      list: state.list || getListDescription({
        breadcrumbs: getPageBreadcrumb(breadcrumbs)?.join(' - '),
        searchTerm,
        appSource: interactionOrigin,
        productId: product.id
      }),
      category: state.category,
      breadcrumbs,
      searchTerm
    }
  }

  setGtmCartProductVariant(product.id, { list: cachedGtmDetailContext.list })
  const stockStatus = variant
    ? getProductStockStatus(
        inventory.value?.variants[variant.id]?.stockStatus,
        variant.productInventoryState
      )
    : undefined

  $gtm.push('product.onProductDetailView', product, {
    viewType: 'PDP',
    ...cachedGtmDetailContext,
    appSource: interactionOrigin,
    preserveListAndCategory: true,
    selectedVariant: variant,
    stockStatus,
    reviewsPageId: reviewsData.value?.reviewsPageId
  })
}

provide(PdpContextKey, {
  addFavorite,
  addToCart,
  addToCartLoading,
  attributeSelection,
  attributeSelectionFormRef: formRef,
  attributeSelectionLabels,
  bundleAddToCartLabel,
  bundleItemCount,
  bundlePromo,
  bundleSelection,
  bundleUpsells,
  defaultVariant,
  dialogNotification,
  eSpots,
  error,
  eyebrowStyle,
  findInStore,
  fitRating,
  galleryItems,
  galleryPending,
  getAttrOptionAvailable,
  getAttrOptionNotifyMe,
  getMatchingVariant,
  giftCardForm,
  handleAddToCart,
  handleApplePay,
  handleNextAttribute,
  handleNotifyMe,
  handleInStore,
  hasFitFinderContent,
  inventory,
  inventoryError,
  isComingSoon,
  isDigitalProduct,
  isFranchisePdp,
  isFavorite,
  isFavoritePending,
  isNonLoyaltyMember,
  isNotifyMe,
  isOutOfStock,
  isRobisOnly,
  isSelectedVariantOutOfStock,
  isSignInToBuy,
  modelMeasurements,
  nextAttribute,
  onZoom,
  openQuickshop,
  openShopTheLook,
  pending,
  product: displayProduct,
  productColor,
  productError,
  productId,
  productPending,
  productPrice,
  previewColor,
  questionsData,
  questionsError,
  questionsPending,
  questionsQuery,
  removeBundleSelection,
  removeFavorite,
  resetNotification,
  reviewsData,
  reviewsError,
  reviewsPending,
  reviewsQuery,
  reviewsSummaryData,
  selectColor,
  selectSize,
  setColorPreview,
  clearColorPreview,
  selectedVariant,
  setNotification,
  shippingMethod,
  stickyCtaDisplay,
  showFavorites,
  showFindInStore,
  showFitFinder,
  showFitRating,
  showModelMeasurements,
  showShopTheLook,
  showSizeChart,
  triggerApplePaySession,
  upsertBundleSelection,
  variantRequestPending
})

watch(productId, clearColorPreview)

watch(pending, () => {
  if (isServer() || pending.value) return

  history.state.skuChange && preselectAttributes()
})

watch([() => auth.loggedIn, () => user.pending, () => loyalty.vouchersPending], () => {
  if (auth.loggedIn && !user.pending && !loyalty.vouchersPending)
    displayLoyaltyToast()
}, { immediate: true })

onMounted(async () => {
  // if condition can be removed in GLOBAL15-68785 and until condition will be updated
  if (productPending.value)
    await until(productPending).toBe(false)

  const hasUtmParam = Object.keys(route.query).some((key) => key.startsWith('utm_'))
  setInteractionOrigin(hasUtmParam ? 'direct-to-pdp' : interactionOrigin)
  setCategoryMetatags()

  if (!history.state.skuChange) {
    trackEvent('product_view', {
      appSource: interactionOrigin,
      product: product.value
    })
    $gtm.queueEvent('page.onLoadPageData', route, {
      breadcrumbs: breadcrumbs.value,
      categoryId: product.value?.primaryCategoryId?.toString(),
      pageNameEng: product.value?.productNameEng
        ? `${productId.value} : ${product.value.productNameEng}`
        : pageMetaTags.value?.defaultPageName?.content,
      pageTitle: await getPageTitle(),
      searchType: history.state.searchType
    })
    const userEventData = await getUserEventData()
    $gtm.queueEvent('user.onLoadUserData', userEventData)
    $gtm.queueEvent('page.onRouterChange', route)
    $gtm.queueEvent('cart.onCartLoad', cart)
    $gtm.flushQueue()
    defineUser()
    $optimizely?.dataReady(userEventData)
    trackEvent('page_view', { URL: route.fullPath })
  }

  if ($feature.showUpsellsOnPdp && !hideUpsells.value?.pdp && product.value?.upsell?.length) {
    $gtm.queueEvent('upsell.onUpsellImpression', product.value.upsell)
  }

  if ($feature.enablePreloadColorsOnPDP && !history.state.skuChange) {
    const requestIdleCallback = window.requestIdleCallback || setTimeout

    requestIdleCallback(async () => {
      const colorAttr = product.value?.attributes.find(({ type }) => type === 'color')
      const otherColorOptions = colorAttr?.options.filter(({ id }) => id !== productId.value)
      const otherColorIds = otherColorOptions?.map(({ id }) => id).filter(Boolean)

      // This is done, so the responses get stored in the cache storage
      // (check the runtimeCache config in domains/core/config/nuxt/pwa.ts),
      // speeding up the response when customers select other colors.
      const preloadQueue = (otherColorIds || []).map((colorId = '') => products.$details.bind(null, colorId, {}, { headers: { Preload: 'true' } }))

      for (let i = 0; i < preloadQueue.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        preloadQueue[i]()
      }
    })
  }

  whenever(() => product.value?.id, async () => {
    if (inventoryPending.value)
      await until(inventoryPending).toBe(false)
    if ($feature.showReviewsOnPDP)
      await until(reviewsPending).toBe(false)

    pushProductDetailViewEvent(product.value)
  }, { immediate: true })

  // Push updated event with inventory data once it becomes available
  watch(inventory, () => {
    if (inventory.value && product.value) {
      injectFitFinder(product as Ref<Product>, {
        addToCart,
        selectSize,
        cartItems: computed(() => cart.items),
        attributeSelection,
        enableColorAsAttribute,
        inventory,
        getAddToCartBtn: () => window['pdp-add-to-cart'],
        getSizeChartLink: () => window['pdp-size-chart']
      })
    }
  }, { immediate: true })

  preselectAttributes()
})

onBeforeUnmount(() => {
  resetInteractionOrigin()
  seoMetaHead.value?.dispose()
  header.transparent = false
})

onDeactivated(() => {
  resetInteractionOrigin()
  seoMetaHead.value?.dispose()
})
</script>
